import type { DashboardState } from '../types';

function fmtAge(ms: number): string {
  if (!ms) return '—';
  const ageS = (Date.now() - ms) / 1000;
  if (ageS < 60) return `${Math.round(ageS)}s ago`;
  if (ageS < 3600) return `${Math.round(ageS / 60)}m ago`;
  if (ageS < 86400) return `${Math.round(ageS / 3600)}h ago`;
  return `${Math.round(ageS / 86400)}d ago`;
}

function fmtUptime(s: number): string {
  if (!s) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

interface Props { state: DashboardState; }

export function ProcessPanel({ state }: Props) {
  return (
    <div className="card">
      <h3>Process</h3>
      <table>
        <tbody>
          <tr><td>Uptime</td><td>{state.health ? fmtUptime(state.health.uptime) : '—'}</td></tr>
          <tr><td>Status</td><td>{state.health?.status || 'unreachable'}</td></tr>
          <tr><td>Circuit</td><td>{state.circuit.consecutive529} consecutive 529s</td></tr>
          <tr><td>Worktrees</td><td>{state.worktrees.count} on disk</td></tr>
          {state.worktrees.oldest && (
            <tr><td>Oldest</td><td>{state.worktrees.oldest.name} ({fmtAge(state.worktrees.oldest.mtimeMs)})</td></tr>
          )}
        </tbody>
      </table>
      {state.errors.length > 0 && (
        <div className="errors">
          <strong>Aggregator errors:</strong>
          <ul>{state.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export function QueuePanel({ state }: Props) {
  const q = state.queue;
  if (!q) return <div className="card"><h3>Queue</h3><p>unreachable</p></div>;
  return (
    <div className="card">
      <h3>Queue</h3>
      {state.pid && (
        <p><strong>Currently running:</strong> #{state.pid.issueNumber} ({state.pid.profile}, since {new Date(state.pid.spawnedAt).toLocaleTimeString()})</p>
      )}
      {q.queue.length === 0 ? (
        <p className="muted">no pending jobs</p>
      ) : (
        <table>
          <thead><tr><th>#</th><th>Title</th><th>Workflow</th><th>Source</th><th>Pri</th></tr></thead>
          <tbody>
            {q.queue.map(j => (
              <tr key={j.issueNumber}>
                <td>#{j.issueNumber}</td>
                <td>{j.title}</td>
                <td>{j.workflow}</td>
                <td>{j.source}</td>
                <td>{j.priority}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function PrsPanel({ state }: Props) {
  return (
    <div className="card">
      <h3>Open Skynet PRs ({state.openSkynetPrs.length})</h3>
      {state.openSkynetPrs.length === 0 ? (
        <p className="muted">none</p>
      ) : (
        <ul>
          {state.openSkynetPrs.map(pr => (
            <li key={pr.number}>
              <a href={pr.url} target="_blank" rel="noreferrer">#{pr.number}</a>
              {' '}{pr.title}
              <span className="muted"> ({fmtAge(new Date(pr.createdAt).getTime())})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PawnReadyPanel({ state }: Props) {
  return (
    <div className="card">
      <h3>Pawn-ready issues ({state.pawnReadyIssues.length})</h3>
      {state.pawnReadyIssues.length === 0 ? (
        <p className="muted">none</p>
      ) : (
        <ul>
          {state.pawnReadyIssues.map(i => {
            const labels = i.labels.map(l => l.name).join(', ');
            const blocked = labels.includes('blocked');
            return (
              <li key={i.number}>
                <a href={`https://github.com/egoramplysales/amplysales-crm/issues/${i.number}`} target="_blank" rel="noreferrer">
                  #{i.number}
                </a>
                {' '}{i.title}
                <span className={blocked ? 'tag blocked' : 'tag'}>{labels}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
